import React, { FC, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { Button } from 'reactstrap';
import { Person } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  downloadLink,
  getFriendlyErrorMessageIfErrorOccurred,
  getPrettyDate,
} from 'utils/util/util';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';

type Props = {
  isOpen: boolean;
  documentId: number;
  onClosed: () => void;
  onDelete: (document: any) => void;
};
const DeleteDocumentModal: FC<Props> = ({
  isOpen,
  documentId,
  onClosed,
  onDelete,
}) => {
  const proxyParameters = useProxyParameters();
  const { formatMessage, locale } = useIntl();
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );

  const { data, error, status } = useConfirmApi<any>({
    url: `documents/${documentId}`,
    method: 'GET',
    disabled: !isOpen,
    clearOnChange: true,
    clearOnError: true,
  });

  const handleOnDeleteCallback = useCallback(() => {
    ConfirmAPI.sendRequestToConfirm(
      'DELETE',
      `documents/${documentId}`,
      proxyParameters,
      (data, error: unknown, hardErrorMessage: unknown) => {
        if (error || hardErrorMessage) {
          setErrorMessage(
            getFriendlyErrorMessageIfErrorOccurred(error, hardErrorMessage)
          );
          return;
        }
        onDelete(data);
        onClosed();
      }
    );
  }, [documentId, onClosed, onDelete, proxyParameters]);

  useEffect(() => {
    if (status === 'ERROR') {
      // @ts-expect-error
      if (error?.status === 404) {
        toast.warn(
          formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.delete_modal.error.document_not_owned',
            defaultMessage:
              'You do not have permission to delete this document. It may have already been deleted or you may have lost the ownership, since belongs to people you may not have admin capability against anymore.',
          })
        );
      } else {
        toast.error(
          formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.delete_modal.error.fetching_document_data',
            defaultMessage: 'Error fetching document data',
          })
        );
      }
    }
  }, [error, formatMessage, status]);

  const handleDownload = useCallback((file: string) => {
    downloadLink({
      filename: file.split('/').pop() || 'document',
      url: file,
    });
  }, []);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.delete_modal.message.fetching_document_data',
          defaultMessage: 'Fetching document data',
        })}
      />
    );
  }

  return (
    <>
      {!!data && (
        <ConfirmationDialogModal
          isOpen={isOpen}
          onClosed={onClosed}
          toggle={onClosed}
          confirmCallback={handleOnDeleteCallback}
          title={formatMessage(
            {
              id: 'app.views.administration.content_administration.documents_admin.delete_modal.title.delete_document',
              defaultMessage:
                'Delete document owned by {ownersNames}, created on {creationDate}',
            },
            {
              ownersNames: data.owners
                .map((owner: Person) => owner.full_name)
                .join(', '),
              creationDate: getPrettyDate({
                dateString: data.created_at,
                locale,
              }),
            }
          )}
          description={
            <div>
              <div className="mb-4">
                {formatMessage({
                  id: 'app.views.administration.content_administration.documents_admin.delete_modal.description.delete_document',
                  defaultMessage:
                    'Are you sure that you want to remove this document?',
                })}
              </div>
              <div>
                {formatMessage(
                  {
                    id: 'app.views.administration.content_administration.documents_admin.delete_modal.description.current_file',
                    defaultMessage: 'File: {filename} <link>Download</link> ',
                  },
                  {
                    filename: data.media.file.split('/').pop(),
                    link: (chunks: React.ReactNode) => (
                      <Button
                        color="link"
                        size="sm"
                        onClick={() => handleDownload(data.media.file)}
                        className="p-0"
                      >
                        <i className="fe fe-download me-1" />
                        {chunks}
                      </Button>
                    ),
                  }
                )}
              </div>
            </div>
          }
          confirmText={formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.delete_modal.confirm_text.delete',
            defaultMessage: 'Delete',
          })}
          validationErrors={errorMessage}
        />
      )}
    </>
  );
};

export default React.memo(DeleteDocumentModal);
